import React, { createContext, useContext, useState, useEffect } from 'react'
import { auth, initAppCheckIfNeeded } from '../firebase/config'
import { ensureAppCheckToken } from '../firebase/appCheck'
import { onAuthStateChanged, signInAnonymously, signOut } from 'firebase/auth'

import { isBotOrLighthouse } from '../utils/userAgent'

const AuthContext = createContext()

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let isMounted = true
    let unsubscribe = null

    const initializeAuth = () => {
      if (isBotOrLighthouse()) {
        console.log('[AuthContext] Bot/Lighthouse detected, skipping auth...')
        if (isMounted) setLoading(false)
        return
      }

      console.log('[AuthContext] Setting up listener (deferred)...')
      
      // Ensure App Check is initialized for all real users (new and returning)
      // Otherwise returning users skip ensureAppCheckToken() and fail Cloud Functions
      initAppCheckIfNeeded()

      unsubscribe = onAuthStateChanged(
        auth,
        async (currentUser) => {
          if (!isMounted) return

          if (currentUser) {
            console.log('[AuthContext] User detected:', currentUser.uid)
            setUser(currentUser)
            setLoading(false)
          } else {
            console.log('[AuthContext] No user, signing in anonymously...')
            try {
              await ensureAppCheckToken()
              const cred = await signInAnonymously(auth)
              console.log('[AuthContext] Anonymous sign-in success:', cred.user.uid)
              // No need to set state here, onAuthStateChanged will fire again
            } catch (err) {
              console.error('[AuthContext] Anonymous sign-in failed:', err)
              if (isMounted) {
                setError(err.message)
                setLoading(false)
              }
            }
          }
        },
        async (err) => {
          console.error('[AuthContext] Auth observer error:', err)
          if (err.code === 'auth/invalid-refresh-token' || err.message?.includes('invalid-refresh-token')) {
            console.warn('[AuthContext] Invalid refresh token detected, attempting signOut to clear storage...')
            try {
              await signOut(auth)
            } catch (signOutErr) {
              console.error('[AuthContext] signOut failed in observer error handler:', signOutErr)
            }
          }
          if (isMounted) {
            setError(err.message)
            setLoading(false)
          }
        }
      )
    }

    let authInitialized = false
    const loadAuth = () => {
      if (authInitialized || !isMounted) return
      authInitialized = true
      initializeAuth()

      ;['mousemove', 'touchstart', 'keydown', 'click'].forEach(e => {
        window.removeEventListener(e, loadAuth)
      })
    }

    ;['mousemove', 'touchstart', 'keydown', 'click'].forEach(e => {
      window.addEventListener(e, loadAuth, { passive: true, once: true })
    })

    return () => {
      isMounted = false
      ;['mousemove', 'touchstart', 'keydown', 'click'].forEach(e => {
        window.removeEventListener(e, loadAuth)
      })
      if (unsubscribe) unsubscribe()
    }
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, error }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
